/**
 * Score annotation extension handler.
 *
 * Scores use EAV pattern: each score field becomes a row in annotation_data.
 * Supports linking to other annotations (masks/vectors).
 */
import { BaseExtensionHandler } from "../_base.ts"
import type { ExtensionManifest, SerializedAnnotation, StoredAnnotation } from "../_interface.ts"
import { createScoreSchema, scoreConfigSchema, updateScoreSchema } from "./schema.ts"

export class ScoreHandler extends BaseExtensionHandler {
  readonly type = "score"
  readonly manifest: ExtensionManifest = {
    type: "score",
    label: "Score / Rating",
    scope: "case",
    storage: "structured",
    supportsLinking: true
  }

  getConfigSchema() {
    return scoreConfigSchema
  }

  getCreateSchema() {
    return createScoreSchema
  }

  getUpdateSchema() {
    return updateScoreSchema
  }

  serializeForDb(apiData: unknown): SerializedAnnotation {
    const data = apiData as {
      linked_annotation_id?: string | null
      values?: Array<{
        field_key: string
        value_text?: string | null
        value_int?: number | null
        value_float?: number | null
        value_bool?: boolean | null
      }>
    }

    const eavRows = (data.values ?? []).map((v) => ({
      field_key: v.field_key,
      value_text: v.value_text ?? null,
      value_int: v.value_int ?? null,
      value_float: v.value_float ?? null,
      value_bool: v.value_bool != null ? (v.value_bool ? 1 : 0) : null
    }))

    return {
      annotation: {
        linked_annotation_id: data.linked_annotation_id ?? null
      },
      data: eavRows,
      files: []
    }
  }

  deserializeFromDb(stored: StoredAnnotation): unknown {
    const values = stored.data.map((d) => ({
      id: d.id,
      field_key: d.field_key,
      value_text: d.value_text,
      value_int: d.value_int,
      value_float: d.value_float,
      value_bool: d.value_bool
    }))

    return {
      id: stored.annotation.id,
      submission_id: stored.annotation.submission_id,
      project_module_id: stored.annotation.project_module_id,
      series_id: stored.annotation.series_id,
      linked_annotation_id: stored.annotation.linked_annotation_id,
      values,
      created_at: stored.annotation.created_at,
      updated_at: stored.annotation.updated_at
    }
  }

  getExportColumns(templateSchema: unknown): string[] {
    const schema = templateSchema as { fields?: Array<{ key: string }> }
    return (schema.fields ?? []).map((f) => f.key)
  }

  toExportRow(annotations: StoredAnnotation[], templateSchema: unknown): Record<string, string | number> {
    const columns = this.getExportColumns(templateSchema)
    const pivoted: Record<string, string | number> = {}
    const sortedAnnotations = [...annotations].sort((a, b) => {
      if (a.annotation.created_at === b.annotation.created_at) {
        return a.annotation.id.localeCompare(b.annotation.id)
      }
      return a.annotation.created_at.localeCompare(b.annotation.created_at)
    })

    for (const col of columns) {
      pivoted[col] = ""
    }

    for (const annotation of sortedAnnotations) {
      for (const d of annotation.data) {
        if (columns.includes(d.field_key)) {
          const value =
            d.value_int !== null
              ? d.value_int
              : d.value_float !== null
                ? d.value_float
                : d.value_bool !== null
                  ? d.value_bool
                  : (d.value_text ?? "")
          const existing = pivoted[d.field_key]
          if (existing === "" || existing === null || existing === undefined) {
            pivoted[d.field_key] = value
          } else {
            pivoted[d.field_key] = `${String(existing)}|${String(value)}`
          }
        }
      }
    }

    return pivoted
  }
}
